/**
 * **cash-sync** gamification — domain layer (app-specific).
 *
 * The core (`engine.ts`) is agnostic; here we define WHAT awards rewards in
 * finance. **Positive and optional** gamification rewarding financial
 * CONSISTENCY (recording transactions, opening wallets), NEVER amounts:
 * this module counts events but never reads `amount`/balances (privacy §
 * cash-sync). Private leagues by default.
 *
 * LOCAL-FIRST source: live `tx` and `wallet` tables (not `activity_recent`,
 * which is a bucket projected by the server). The reducer is pure and
 * deterministic → instant feedback, including for guests without sync.
 */
import { counterAtLeast, levelAtLeast, streakAtLeast } from "./badges";
import { applyEvents, type GamificationConfig, initialState } from "./engine";
import { computeStreak, dayKeyUTC } from "./streaks";
import type { GameEvent, PlayerState } from "./types";

/** Streak line: "days with at least one transaction recorded". */
export const STREAK_FINANCE = "finance";

/**
 * CashSync `GamificationConfig`. Only positive events award XP. No
 * amount is involved: XP is fixed per event, never proportional to money.
 */
export const CASHSYNC_GAMIFICATION: GamificationConfig = {
  xp: {
    "tx.recorded": 5,
    "wallet.created": 5,
  },
  streakLines: {
    "tx.recorded": [STREAK_FINANCE],
  },
  counters: {
    "tx.recorded": ["movements"],
    "wallet.created": ["wallets"],
  },
  badges: [
    { id: "first-movement", title: "Primer movimiento", when: counterAtLeast("movements", 1) },
    { id: "ten-movements", title: "Al día", when: counterAtLeast("movements", 10) },
    {
      id: "fifty-movements",
      title: "Contable",
      when: counterAtLeast("movements", 50),
      bonusXp: 25,
    },
    {
      id: "century-movements",
      title: "Libro mayor",
      when: counterAtLeast("movements", 100),
      bonusXp: 50,
    },
    { id: "first-wallet", title: "Primer monedero", when: counterAtLeast("wallets", 1) },
    {
      id: "week-streak",
      title: "Semana constante",
      when: streakAtLeast(STREAK_FINANCE, 7),
      bonusXp: 40,
    },
    {
      id: "month-streak",
      title: "Mes al día",
      when: streakAtLeast(STREAK_FINANCE, 30),
      bonusXp: 100,
    },
    { id: "level-5", title: "Nivel 5", when: levelAtLeast(5) },
    { id: "level-10", title: "Nivel 10", when: levelAtLeast(10) },
  ],
  quests: [
    {
      id: "daily-1",
      title: "Registra un movimiento hoy",
      period: "daily",
      goals: [{ metric: "tx.recorded", target: 1 }],
      bonusXp: 10,
    },
    {
      id: "weekly-5",
      title: "5 movimientos esta semana",
      period: "weekly",
      goals: [{ metric: "tx.recorded", target: 5 }],
      bonusXp: 30,
    },
  ],
};

/** Badge catalog (for rendering badges not yet earned, in gray). */
export const CASHSYNC_BADGES = CASHSYNC_GAMIFICATION.badges ?? [];

// ── LOCAL-FIRST derivation from `tx` / `wallet` tables ────────────────

/** Minimum `tx` row needed by gamification (no amounts). */
export interface GamifiableTx {
  occurred_at: string | null;
  /** Fallback if `occurred_at` is missing; might not come in projection. */
  created_at?: string | null;
  /** Balance adjustments (`is_adjustment=1`) DO NOT count as user transactions. */
  is_adjustment: number | null;
}

/**
 * Derives game events from live rows. Each real transaction (non-adjustment)
 * emits `tx.recorded` (day = `occurred_at`, transaction date).
 * Wallets only contribute their COUNT (`wallet.created` feeds no streak →
 * the day is indifferent; it serves XP/badge consistency). Pure and deterministic.
 * DOES NOT read amounts or balances.
 */
export function financeRowsToGameEvents(
  txs: readonly GamifiableTx[],
  walletCount = 0,
): GameEvent[] {
  const events: GameEvent[] = [];
  for (let i = 0; i < walletCount; i += 1) {
    events.push({ type: "wallet.created", at: 0, day: dayKeyUTC(0) });
  }
  for (const t of txs) {
    if (t.is_adjustment) {
      continue;
    }
    const stamp = t.occurred_at ?? t.created_at;
    const at = stamp ? Date.parse(stamp) : Number.NaN;
    if (!Number.isNaN(at)) {
      events.push({ type: "tx.recorded", at, day: dayKeyUTC(at) });
    }
  }
  events.sort((a, b) => a.at - b.at);
  return events;
}

/**
 * Recomputes player state from `tx`/`wallet` (local-first). Re-anchors
 * financial streak to real "today". Pure.
 *
 * `freezes` are the AVAILABLE streak freezes. This is the growth kit entry
 * point: a rewarded ad that grants `streak-freeze` is translated here
 * (`adFreezes(rewardsStore)`) without the gamification engine knowing that
 * ads exist.
 */
export function computePlayerFromFinance(
  txs: readonly GamifiableTx[],
  walletCount = 0,
  todayKey: string = dayKeyUTC(Date.now()),
  freezes = 0,
): { state: PlayerState; events: number } {
  const events = financeRowsToGameEvents(txs, walletCount);
  const { state } = applyEvents(initialState(freezes), events, CASHSYNC_GAMIFICATION);
  const active = state.activeDays[STREAK_FINANCE] ?? [];
  if (active.length > 0) {
    const streak = computeStreak(active, todayKey, { freezes: state.freezes });
    streak.lastDay = active.reduce((max, d) => (d > max ? d : max), active[0] ?? "");
    state.streaks[STREAK_FINANCE] = streak;
  }
  return { state, events: events.length };
}
